import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  Textsms as TextsmsIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { useTheme } from '@mui/material/styles';
import ProLeadConversation from './ProLeadConversation';

interface ProLead {
  full_name: string;
  phone: string;
}

interface SavedLead extends ProLead {
  id: string;
  created_at: string;
  message_status: string | null;
  opt_in: boolean | null;
}

const BURST_FROM_NUMBERS = [
  '+18138693257',
  '+18137380841',
  '+18137308723',
  '+18137308127',
  '+18133039273',
  '+18138458139',
  '+18132198893',
  '+18132198831',
  '+18132198736',
  '+18132198539',
];

function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return '';
}

function getBurstMessage(fullName: string): string {
  return `Hey ${fullName}, we saw you applied to our remote sales job. I think you could be a great fit. Can I send you some of the info? Reply YES for more info\nReply STOP to opt-out`;
}

function getRandomFromNumber(): string {
  return BURST_FROM_NUMBERS[Math.floor(Math.random() * BURST_FROM_NUMBERS.length)];
}

interface Props {
  ownerProfile: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const ALLOWED_ORG_IDS = [1];

const ProLeadGenerator: React.FC<Props> = ({ ownerProfile }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  // Add Lead state
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [leads, setLeads] = useState<ProLead[]>([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [recentlySaved, setRecentlySaved] = useState<SavedLead[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [allLeads, setAllLeads] = useState<SavedLead[]>([]);
  const [allLeadsLoading, setAllLeadsLoading] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');

  // Burst Text state
  const [selectedBurstIds, setSelectedBurstIds] = useState<Set<string>>(new Set());
  const [burstSending, setBurstSending] = useState(false);
  const [burstProgress, setBurstProgress] = useState({ sent: 0, total: 0 });

  // Prompt state
  const [promptId, setPromptId] = useState<number | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyDirty, setReplyDirty] = useState(false);
  const [replySaving, setReplySaving] = useState(false);

  // Burst Send state
  const [burstSendingAPI, setBurstSendingAPI] = useState(false);

  const csvInputRef = useRef<HTMLInputElement>(null);

  const orgId = ownerProfile?.org_id;

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      const lines = text.split(/\r?\n/).filter(line => line.trim());
      const parsed: ProLead[] = [];
      let skipped = 0;

      for (const line of lines) {
        const cols = line.includes('\t') ? line.split('\t') : line.split(',');
        const rawName = cols[0]?.trim().replace(/^["']|["']$/g, '');
        const rawPhone = cols[1]?.trim().replace(/^["']|["']$/g, '');
        if (!rawName || !rawPhone) { skipped++; continue; }

        const normalized = normalizePhone(rawPhone);
        if (!normalized) { skipped++; continue; }

        parsed.push({ full_name: rawName, phone: normalized });
      }

      if (parsed.length > 0) {
        setLeads(prev => [...prev, ...parsed]);
        showSnackbar(`${parsed.length} lead(s) imported${skipped > 0 ? `, ${skipped} row(s) skipped` : ''}`, 'success');
      } else {
        showSnackbar('No valid leads found in CSV. Ensure Column A has names and Column B has phone numbers.', 'error');
      }
    };
    reader.readAsText(file);

    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchAllLeads = useCallback(async () => {
    if (!orgId) return;
    try {
      setAllLeadsLoading(true);
      const allData: SavedLead[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        allData.push(...(data || []));
        hasMore = (data?.length ?? 0) === pageSize;
        from += pageSize;
      }

      setAllLeads(allData);
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      showSnackbar('Error loading leads: ' + err.message, 'error');
    } finally {
      setAllLeadsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchAllLeads();
  }, [fetchAllLeads]);

  const fetchOrCreatePrompt = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from('lead_text_prompt')
        .select('*')
        .eq('org_id', orgId)
        .limit(1)
        .single();

      if (error && error.code === 'PGRST116') {
        const hardcodedInitial = getBurstMessage('{Full Name}');
        const { data: inserted, error: insertErr } = await supabase
          .from('lead_text_prompt')
          .insert({ org_id: orgId, initial_message: hardcodedInitial, reply_message: '' })
          .select()
          .single();
        if (insertErr) throw insertErr;
        if (inserted) {
          setPromptId(inserted.id);
          setReplyMessage(inserted.reply_message || '');
        }
      } else if (error) {
        throw error;
      } else if (data) {
        setPromptId(data.id);
        setReplyMessage(data.reply_message || '');
      }
    } catch (err: any) {
      console.error('Error fetching prompt:', err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrCreatePrompt();
  }, [fetchOrCreatePrompt]);

  const handleSaveReply = async () => {
    if (!promptId) return;
    try {
      setReplySaving(true);
      const { error } = await supabase
        .from('lead_text_prompt')
        .update({ reply_message: replyMessage })
        .eq('id', promptId);
      if (error) throw error;
      setReplyDirty(false);
      showSnackbar('Auto-reply saved', 'success');
    } catch (err: any) {
      showSnackbar('Error saving auto-reply: ' + err.message, 'error');
    } finally {
      setReplySaving(false);
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setAllLeads((prev) => prev.filter((l) => l.id !== id));
      setRecentlySaved((prev) => prev.filter((l) => l.id !== id));
      showSnackbar('Lead deleted', 'success');
    } catch (err: any) {
      showSnackbar('Error deleting lead: ' + err.message, 'error');
    }
  };


  const handleAddLead = useCallback(() => {
    const trimmedName = fullName.trim();
    const normalized = normalizePhone(phone);

    if (!trimmedName) {
      showSnackbar('Full name is required', 'error');
      return;
    }
    if (!normalized) {
      showSnackbar('Enter a valid 10-digit US phone number', 'error');
      return;
    }

    setLeads((prev) => [...prev, { full_name: trimmedName, phone: normalized }]);
    setFullName('');
    setPhone('');
  }, [fullName, phone]);

  const handleRemoveLead = (index: number) => {
    setLeads((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAll = async () => {
    if (leads.length === 0) {
      showSnackbar('Add at least one lead before saving', 'error');
      return;
    }
    if (!orgId) {
      showSnackbar('Organization not found', 'error');
      return;
    }

    try {
      setSaving(true);

      const phoneNumbers = leads.map(l => l.phone);
      const { data: existing } = await supabase
        .from('leads')
        .select('phone')
        .eq('org_id', orgId)
        .in('phone', phoneNumbers);

      const existingPhones = new Set((existing || []).map(r => r.phone));
      const duplicates = leads.filter(l => existingPhones.has(l.phone));

      if (duplicates.length > 0) {
        const dupList = duplicates.map(d => `${d.full_name} (${d.phone})`).join(', ');
        showSnackbar(`These numbers already exist: ${dupList}`, 'error');
        setSaving(false);
        return;
      }

      const rows = leads.map((lead) => ({
        full_name: lead.full_name,
        phone: lead.phone,
        org_id: orgId,
      }));

      const { data, error } = await supabase
        .from('leads')
        .insert(rows)
        .select();

      if (error) throw error;

      showSnackbar(`${leads.length} lead(s) saved successfully`, 'success');
      setRecentlySaved((prev) => [...(data || []), ...prev]);
      setLeads([]);
      fetchAllLeads();
    } catch (err: any) {
      console.error('Error saving leads:', err);
      showSnackbar('Error saving leads: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPhone = (phoneVal: string, id?: string) => {
    navigator.clipboard.writeText(phoneVal).then(() => {
      if (id) {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
      }
      showSnackbar('Phone copied to clipboard', 'success');
    });
  };

  const handleCopyName = (name: string) => {
    navigator.clipboard.writeText(name).then(() => {
      showSnackbar('Name copied to clipboard', 'success');
    });
  };

  // Burst Text logic
  const createdLeads = allLeads.filter(
    (l) => !l.message_status || l.message_status.toLowerCase() === 'created'
  );

  const handleToggleBurstSelect = (id: string) => {
    setSelectedBurstIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllBurst = () => {
    if (selectedBurstIds.size === createdLeads.length) {
      setSelectedBurstIds(new Set());
    } else {
      setSelectedBurstIds(new Set(createdLeads.map((l) => l.id)));
    }
  };

  const handleBurstSend = async () => {
    const selected = createdLeads.filter((l) => selectedBurstIds.has(l.id));
    if (selected.length === 0) {
      showSnackbar('Select at least one lead to send', 'error');
      return;
    }
    if (!orgId) {
      showSnackbar('Organization not found', 'error');
      return;
    }

    setBurstSending(true);
    setBurstProgress({ sent: 0, total: selected.length });

    const BATCH_SIZE = 500;
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selected.length; i += BATCH_SIZE) {
      const batch = selected.slice(i, i + BATCH_SIZE);

      const textRows = batch.map((lead) => ({
        org_id: orgId,
        lead_id: lead.id,
        number: lead.phone,
        from_number: getRandomFromNumber(),
        message: getBurstMessage(lead.full_name),
        direction: 'outbound',
        status: 'queued',
      }));

      try {
        const { error: insertErr } = await supabase.from('lead_texts').insert(textRows);
        if (insertErr) throw insertErr;

        const batchIds = batch.map((l) => l.id);
        const { error: updateErr } = await supabase
          .from('leads')
          .update({ message_status: 'queued' })
          .in('id', batchIds);
        if (updateErr) throw updateErr;

        successCount += batch.length;
      } catch (err: any) {
        console.error(`Batch insert failed (offset ${i}):`, err);
        failCount += batch.length;
      }

      setBurstProgress({ sent: Math.min(i + BATCH_SIZE, selected.length), total: selected.length });
    }

    setAllLeads((prev) => {
      const queuedIds = new Set(selected.filter((_, idx) => idx < successCount).map((l) => l.id));
      return prev.map((l) => queuedIds.has(l.id) ? { ...l, message_status: 'queued' } : l);
    });

    setBurstSending(false);
    setSelectedBurstIds(new Set());

    if (failCount === 0) {
      showSnackbar(`Burst queued for ${successCount} lead(s)`, 'success');
    } else {
      showSnackbar(`${successCount} queued, ${failCount} failed`, 'error');
    }
  };

  const handleBurstSendAPI = async () => {
    if (!orgId) {
      showSnackbar('Organization not found', 'error');
      return;
    }
    try {
      setBurstSendingAPI(true);
      const internalKey = process.env.REACT_APP_INTERNAL_KEY;
      if (!internalKey) {
        showSnackbar('Internal gateway token not configured', 'error');
        return;
      }

      const response = await fetch('https://hr-backend-pipeline.vercel.app/api/telnyx/sendBurstSMS', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${internalKey}`,
        },
        body: JSON.stringify({ org_id: orgId }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => response.statusText);
        throw new Error(`${response.status} - ${errText}`);
      }

      const data = await response.json();
      showSnackbar(data.message || 'Burst send initiated', 'success');
      fetchAllLeads();
    } catch (err: any) {
      console.error('Burst send error:', err);
      showSnackbar('Burst send failed: ' + err.message, 'error');
    } finally {
      setBurstSendingAPI(false);
    }
  };

  const phonePreview = normalizePhone(phone);

  if (!ALLOWED_ORG_IDS.includes(ownerProfile?.org_id)) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Pro Lead Generator
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
      >
        <Tab icon={<PersonAddIcon />} iconPosition="start" label="Add Lead" />
        <Tab icon={<TextsmsIcon />} iconPosition="start" label="Burst Text" />
        <Tab icon={<ChatIcon />} iconPosition="start" label="Conversations" />
      </Tabs>

      {/* ───────── TAB 1: ADD LEAD ───────── */}
      <TabPanel value={activeTab} index={0}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add a Lead
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Full Name"
              placeholder="John Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const phoneInput = document.getElementById('pro-phone-input');
                  if (phoneInput) phoneInput.focus();
                }
              }}
            />
            <TextField
              id="pro-phone-input"
              label="Phone Number"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText={phone && phonePreview ? `Will save as: ${phonePreview}` : phone ? 'Invalid phone number' : ''}
              error={!!phone && !phonePreview}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddLead();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddLead}
              disabled={!fullName.trim() || !phonePreview}
              sx={{ height: 40 }}
            >
              Add
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => csvInputRef.current?.click()}
              sx={{ height: 40 }}
            >
              Upload CSV
            </Button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              hidden
              onChange={handleCsvUpload}
            />
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            CSV format: Column A = Full Name, Column B = Phone Number. Numbers are auto-formatted to +1XXXXXXXXXX.
          </Typography>
        </Paper>

        {leads.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Pending Leads ({leads.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={() => setLeads([])}
                  size="small"
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveAll}
                  disabled={saving}
                  size="small"
                >
                  {saving ? 'Saving...' : 'Save All'}
                </Button>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone (+1XXXXXXXXXX)</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{lead.full_name}</Typography>
                          <Tooltip title="Copy name">
                            <IconButton size="small" onClick={() => handleCopyName(lead.full_name)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={lead.phone} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                          <Tooltip title="Copy phone">
                            <IconButton size="small" onClick={() => handleCopyPhone(lead.phone)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleRemoveLead(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {recentlySaved.length > 0 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Recently Saved
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">Copy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentlySaved.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{lead.full_name}</Typography>
                          <Tooltip title="Copy name">
                            <IconButton size="small" onClick={() => handleCopyName(lead.full_name)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={lead.phone}
                            size="small"
                            variant="outlined"
                            color={copiedId === lead.id ? 'success' : 'default'}
                            sx={{ fontFamily: 'monospace' }}
                          />
                          <Tooltip title="Copy phone">
                            <IconButton size="small" onClick={() => handleCopyPhone(lead.phone, lead.id)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Copy both">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(`${lead.full_name} ${lead.phone}`);
                              showSnackbar('Name and phone copied', 'success');
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* All Pro Leads from DB */}
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              All Pro Leads ({allLeads.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                placeholder="Search leads..."
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 180 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: leadSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setLeadSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchAllLeads}
                disabled={allLeadsLoading}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>
          {allLeadsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Opt-In</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allLeads
                    .filter((l) => {
                      if (!leadSearch) return true;
                      const q = leadSearch.toLowerCase();
                      return (
                        l.full_name.toLowerCase().includes(q) ||
                        l.phone.toLowerCase().includes(q)
                      );
                    })
                    .map((lead) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{lead.full_name}</Typography>
                            <Tooltip title="Copy name">
                              <IconButton size="small" onClick={() => handleCopyName(lead.full_name)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={lead.phone}
                              size="small"
                              variant="outlined"
                              color={copiedId === lead.id ? 'success' : 'default'}
                              sx={{ fontFamily: 'monospace' }}
                            />
                            <Tooltip title="Copy phone">
                              <IconButton size="small" onClick={() => handleCopyPhone(lead.phone, lead.id)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={lead.message_status || 'N/A'}
                            size="small"
                            variant="outlined"
                            color={
                              lead.message_status === 'sent' ? 'success' :
                              lead.message_status === 'failed' ? 'error' :
                              lead.message_status === 'pending' ? 'warning' :
                              lead.message_status === 'queued' ? 'info' :
                              'default'
                            }
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {lead.opt_in === null ? (
                            <Tooltip title="Pending (waiting for webhook)">
                              <HelpOutlineIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
                            </Tooltip>
                          ) : lead.opt_in ? (
                            <Tooltip title="Opted in">
                              <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Opted out">
                              <CancelIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                            {new Date(lead.created_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Copy both">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(`${lead.full_name} ${lead.phone}`);
                                showSnackbar('Name and phone copied', 'success');
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteLead(lead.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  {allLeads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          No leads saved yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* ───────── TAB 2: BURST TEXT ───────── */}
      <TabPanel value={activeTab} index={1}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Message Preview
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
            }}
          >
            {getBurstMessage('{Full Name}')}
          </Paper>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Each lead&apos;s full name will be substituted automatically. A random sender number is assigned per message.
          </Typography>
        </Paper>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Auto-Reply Message
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={replySaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveReply}
              disabled={!replyDirty || replySaving}
            >
              {replySaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5, display: 'block' }}>
            This message is automatically sent when a lead replies &quot;YES&quot;. Edit and save to customize.
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            value={replyMessage}
            onChange={(e) => {
              setReplyMessage(e.target.value);
              setReplyDirty(true);
            }}
            placeholder="Enter your auto-reply message..."
            size="small"
          />
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Leads with status &quot;Created&quot; ({createdLeads.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchAllLeads}
                startIcon={<RefreshIcon />}
                disabled={allLeadsLoading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={burstSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleBurstSend}
                disabled={burstSending || selectedBurstIds.size === 0}
              >
                {burstSending
                  ? `Queuing ${burstProgress.sent}/${burstProgress.total}...`
                  : `Queue to ${selectedBurstIds.size} selected`}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={burstSendingAPI ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleBurstSendAPI}
                disabled={burstSendingAPI}
              >
                {burstSendingAPI ? 'Sending...' : 'Burst Send'}
              </Button>
            </Box>
          </Box>

          {burstSending && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={burstProgress.total > 0 ? (burstProgress.sent / burstProgress.total) * 100 : 0}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                Sending {burstProgress.sent} of {burstProgress.total}...
              </Typography>
            </Box>
          )}

          {allLeadsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} />
            </Box>
          ) : createdLeads.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No leads with &quot;Created&quot; status found. Add leads and ensure their status is set to &quot;Created&quot;.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedBurstIds.size === createdLeads.length && createdLeads.length > 0}
                        indeterminate={selectedBurstIds.size > 0 && selectedBurstIds.size < createdLeads.length}
                        onChange={handleSelectAllBurst}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {createdLeads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      hover
                      selected={selectedBurstIds.has(lead.id)}
                      onClick={() => handleToggleBurstSelect(lead.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selectedBurstIds.has(lead.id)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lead.full_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={lead.phone} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.message_status || 'Created'}
                          size="small"
                          variant="outlined"
                          color="default"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                          {new Date(lead.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* All Leads overview */}
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            All Leads ({allLeads.length})
          </Typography>
          {allLeadsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} />
            </Box>
          ) : allLeads.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No leads yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allLeads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Typography variant="body2">{lead.full_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={lead.phone} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={lead.message_status || 'N/A'}
                          size="small"
                          variant="outlined"
                          color={
                            lead.message_status === 'sent' ? 'success' :
                            lead.message_status === 'failed' ? 'error' :
                            lead.message_status === 'pending' ? 'warning' :
                            lead.message_status === 'queued' ? 'info' :
                            'default'
                          }
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                          {new Date(lead.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* ───────── TAB 3: CONVERSATIONS ───────── */}
      <TabPanel value={activeTab} index={2}>
        <ProLeadConversation orgId={orgId} />
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProLeadGenerator;
